import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Space, Tooltip } from 'antd';
import { notification } from '../../commonComponents/Notification/Notification';
import { PlusOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import { ConfigProvider } from 'antd';
import {
  Container,
  HeaderSection,
  Title,
  Subtitle,
  PrimaryButton,
  TitleIcon,
  TopControlBar,
  MerchantFilterSelect,
} from './styles';
import CustomInput from '../../commonComponents/input';
import StoreStats from './StoreStats';
import StoreTable from './StoreTable';
import StoreForm from './StoreForm';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import BulkAutoAcceptDropdown from './BulkAutoAcceptDropdown';
import BulkAutoAcceptConfirmDialog from './BulkAutoAcceptConfirmDialog';
import type { StoresProps, StoreFormSubmitPayload } from './types';
import type { Store } from './store/userStoresSlice';
import type { RootState } from '../../redux/store';
import { LABELS, MESSAGES, SUBTITLES, PLACEHOLDERS } from './constants';
import {
  useStores,
  useStoresByMerchant,
  useCreateStore,
  useUpdateStore,
  useDeleteStore,
  useMerchantsList,
  useToggleAutoAccept,
  useBulkAutoAccept,
} from './hooks';

const Stores: React.FC<StoresProps> = () => {
  const { isDarkMode } = useTheme();
  const theme = useMemo(() => ({ mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' }), [isDarkMode]);
  const role = useSelector((state: RootState) => state.auth.role);
  const isAdmin = role === 'admin';
  // The auto-accept toggle's backend endpoint accepts merchant too (unlike
  // create/update/delete, which stay admin-only here) — merchant_manager
  // and merchant_staff are excluded for now.
  const canToggleAutoAccept = isAdmin || role === 'merchant';

  const { data: stores = [], isLoading: storesLoading, isFetching: storesFetching, refetch: refetchStores } = useStores();
  // Only admins ever see stores across more than one merchant, so the
  // merchant filter (and its backing merchant list) is admin-only.
  const { data: merchants = [] } = useMerchantsList(isAdmin);

  const createStoreMutation = useCreateStore();
  const updateStoreMutation = useUpdateStore();
  const deleteStoreMutation = useDeleteStore();
  const toggleAutoAcceptMutation = useToggleAutoAccept();
  const bulkAutoAcceptMutation = useBulkAutoAccept();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [storeToDelete, setStoreToDelete] = useState<{ id: string; name: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [merchantFilter, setMerchantFilter] = useState<string | undefined>(undefined);
  // null = dialog closed; true/false = which bulk action is pending confirmation.
  const [bulkConfirmEnable, setBulkConfirmEnable] = useState<boolean | null>(null);

  // Once an admin picks a merchant, fetch that merchant's stores directly
  // via GET /stores/by-merchant/:id (same endpoint Devices/DeviceAssignment
  // already use) instead of filtering the admin-wide GET /stores list
  // client-side.
  const merchantScoped = isAdmin && !!merchantFilter;
  const {
    data: merchantStores = [],
    isLoading: merchantStoresLoading,
    isFetching: merchantStoresFetching,
  } = useStoresByMerchant(merchantFilter, merchantScoped);

  // GET /stores/by-merchant/:id doesn't embed a `merchant` object on each
  // store the way the unfiltered GET /stores list does (the caller already
  // knows which merchant they filtered to) — StoreTable's "Merchant Name"
  // column reads store.merchant?.name, so without this every row would show
  // blank once a merchant filter is applied. Backfill it from the filter's
  // own merchant list instead.
  const scopedStores = useMemo(() => {
    if (!merchantScoped) return stores;
    const selectedMerchant = merchants.find((m) => m.id === merchantFilter);
    if (!selectedMerchant) return merchantStores;
    return merchantStores.map((store) =>
      store.merchant
        ? store
        : {
            ...store,
            // `username` isn't shown anywhere the table reads this synthetic
            // merchant from — only `.name` is — but StoreMerchant requires it.
            merchant: { id: selectedMerchant.id, name: selectedMerchant.name, username: selectedMerchant.name, role: 'merchant' },
          }
    );
  }, [merchantScoped, stores, merchantStores, merchants, merchantFilter]);
  const scopedStoresLoading = merchantScoped ? merchantStoresLoading : storesLoading;
  const scopedStoresFetching = merchantScoped ? merchantStoresFetching : storesFetching;

  // Admins manage stores across every merchant, so "all locations" is
  // ambiguous until they pick one via the merchant filter — the bulk
  // endpoint requires merchantId for admins for the same reason. Merchants
  // only ever see their own stores, so no filter is needed for them.
  const bulkDisabledReason = isAdmin && !merchantFilter ? MESSAGES.SELECT_MERCHANT_FOR_BULK : undefined;

  // Ignores searchQuery on purpose — the bulk action always targets every
  // store in scope (the admin's selected merchant, or the merchant's own
  // stores), regardless of what's currently typed into the search box.
  const bulkScopeStores = useMemo(() => {
    if (isAdmin) {
      return merchantFilter ? scopedStores : [];
    }
    return stores;
  }, [isAdmin, stores, scopedStores, merchantFilter]);

  const bulkAffectedCount = bulkScopeStores.length;

  // true when every store in scope already has auto-accept on, false when
  // every one already has it off, null when mixed (or scope is empty) — the
  // dropdown highlights whichever option matches this current state.
  const bulkCurrentState = useMemo(() => {
    if (bulkScopeStores.length === 0) return null;
    if (bulkScopeStores.every((s) => s.autoAcceptOrders)) return true;
    if (bulkScopeStores.every((s) => !s.autoAcceptOrders)) return false;
    return null;
  }, [bulkScopeStores]);

  // Merchant scoping now happens server-side (scopedStores above) — this
  // just applies the search box on top of whichever list is in scope.
  const filteredStores = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return scopedStores.filter((store) => {
      const matchesSearch =
        !query ||
        store.name?.toLowerCase().includes(query) ||
        store.address?.toLowerCase().includes(query) ||
        store.city?.toLowerCase().includes(query);
      return matchesSearch;
    });
  }, [scopedStores, searchQuery]);

  const handleCreate = useCallback(() => {
    setSelectedStore(null);
    setIsFormOpen(true);
  }, []);

  const handleEdit = useCallback((store: Store) => {
    setSelectedStore(store);
    setIsFormOpen(true);
  }, []);

  const handleDelete = useCallback((storeId: string) => {
    const store = stores.find((s) => s.id === storeId);
    if (store) {
      setStoreToDelete({ id: storeId, name: store.name });
      setIsDeleteDialogOpen(true);
    }
  }, [stores]);

  const handleFormSubmit = useCallback(
    async (data: StoreFormSubmitPayload) => {
      try {
        if ('id' in data) {
          await updateStoreMutation.mutateAsync(data);
          notification.success(MESSAGES.STORE_UPDATED);
        } else {
          await createStoreMutation.mutateAsync(data);
          notification.success(MESSAGES.STORE_CREATED);
        }
        setIsFormOpen(false);
        setSelectedStore(null);
      } catch (error: any) {
        const msg = error?.response?.data?.message || error?.message || 'Failed to save store';
        notification.error('Error', msg);
      }
    },
    [createStoreMutation, updateStoreMutation]
  );

  const handleToggleAutoAccept = useCallback(
    async (storeId: string, autoAcceptOrders: boolean) => {
      try {
        await toggleAutoAcceptMutation.mutateAsync({ id: storeId, autoAcceptOrders });
        notification.success(MESSAGES.AUTO_ACCEPT_UPDATED);
      } catch (error: any) {
        const msg = error?.response?.data?.message || error?.message || 'Failed to update auto-accept setting';
        notification.error('Error', msg);
      }
    },
    [toggleAutoAcceptMutation]
  );

  const handleBulkAutoAcceptConfirm = useCallback(async () => {
    if (bulkConfirmEnable === null) return;
    try {
      await bulkAutoAcceptMutation.mutateAsync({
        autoAcceptOrders: bulkConfirmEnable,
        ...(isAdmin ? { merchantId: merchantFilter } : {}),
      });
      notification.success(MESSAGES.BULK_AUTO_ACCEPT_UPDATED);
      setBulkConfirmEnable(null);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to update auto-accept setting for all locations';
      notification.error('Error', msg);
    }
  }, [bulkConfirmEnable, bulkAutoAcceptMutation, isAdmin, merchantFilter]);

  const handleFormCancel = useCallback(() => {
    setIsFormOpen(false);
    setSelectedStore(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!storeToDelete) return;
    try {
      await deleteStoreMutation.mutateAsync(storeToDelete.id);
      notification.success(MESSAGES.STORE_DELETED);
      setIsDeleteDialogOpen(false);
      setStoreToDelete(null);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to delete store';
      notification.error('Error', msg);
    }
  }, [deleteStoreMutation, storeToDelete]);

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <div>
              <Title level={1}>
                <TitleIcon>
                  <FontAwesomeIcon icon={faStore} />
                </TitleIcon>
                {LABELS.STORES_MANAGEMENT || 'Stores Management'}
              </Title>
              <Subtitle>
                {isAdmin ? SUBTITLES.ADMIN : SUBTITLES.MERCHANT}
              </Subtitle>
            </div>
            <Space>
              <Tooltip title={isAdmin ? undefined : MESSAGES.MERCHANT_ACTION_DISABLED}>
                {/* antd Tooltip needs a non-disabled element to trigger hover
                    on, since a natively `disabled` button can swallow mouse
                    events — the extra span is required, nothing else changes. */}
                <span style={isAdmin ? undefined : { cursor: 'not-allowed' }}>
                  <PrimaryButton
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={isAdmin ? handleCreate : undefined}
                    disabled={!isAdmin}
                    style={{ height: 44, paddingInline: 28, fontSize: 16 }}
                  >
                    {LABELS.CREATE_STORE}
                  </PrimaryButton>
                </span>
              </Tooltip>
            </Space>
          </HeaderSection>

          <StoreStats stores={filteredStores} />

          {/* Search + merchant filter are admin-only: merchant/manager/staff
              only ever see their own single merchant's stores, so this
              toolbar (and everything it filters) must not render for them. */}
          {isAdmin && (
            <TopControlBar>
              <CustomInput
                placeholder={PLACEHOLDERS.SEARCH_STORES}
                value={searchQuery}
                onChange={(value: string) => setSearchQuery(value)}
                allowClear
                width="320px"
              />
              <MerchantFilterSelect
                showSearch
                allowClear
                placeholder={PLACEHOLDERS.ALL_MERCHANTS}
                value={merchantFilter}
                onChange={(value: string | undefined) => {
                  setMerchantFilter(value);
                  // Reset back to "All Merchants" — refetch the full list
                  // rather than relying on whatever was cached from the last
                  // time it loaded (staleTime: Infinity means it otherwise
                  // never refetches on its own).
                  if (!value) {
                    refetchStores();
                  }
                }}
                options={merchants.map((m) => ({ value: m.id, label: m.name }))}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </TopControlBar>
          )}

          {canToggleAutoAccept && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <BulkAutoAcceptDropdown
                disabled={!!bulkDisabledReason}
                disabledReason={bulkDisabledReason}
                affectedCount={bulkAffectedCount}
                currentState={bulkCurrentState}
                onSelect={(enable) => setBulkConfirmEnable(enable)}
              />
            </div>
          )}

          <StoreTable
            stores={filteredStores}
            loading={scopedStoresLoading || scopedStoresFetching}
            isAdmin={isAdmin}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleAutoAccept={handleToggleAutoAccept}
            canToggleAutoAccept={canToggleAutoAccept}
            togglingStoreId={toggleAutoAcceptMutation.isPending ? toggleAutoAcceptMutation.variables?.id ?? null : null}
          />

          {isFormOpen && (
            <StoreForm
              store={selectedStore}
              loading={createStoreMutation.isPending || updateStoreMutation.isPending}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
            />
          )}

          {isDeleteDialogOpen && storeToDelete && (
            <DeleteConfirmDialog
              open={isDeleteDialogOpen}
              storeName={storeToDelete.name}
              onConfirm={handleDeleteConfirm}
              onCancel={() => {
                setIsDeleteDialogOpen(false);
                setStoreToDelete(null);
              }}
              loading={deleteStoreMutation.isPending}
            />
          )}

          {bulkConfirmEnable !== null && (
            <BulkAutoAcceptConfirmDialog
              open={bulkConfirmEnable !== null}
              enable={bulkConfirmEnable}
              affectedCount={bulkAffectedCount}
              onConfirm={handleBulkAutoAcceptConfirm}
              onCancel={() => setBulkConfirmEnable(null)}
              loading={bulkAutoAcceptMutation.isPending}
            />
          )}
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(Stores);
